import classnames from 'classnames';
import { FC } from 'react';

import { isB2B, isB2C, isUkPlatform } from '@/core/settings/utils';
import {
  CurrencyType,
  FormatType,
  Price as TamagoshiPrice,
  VatType,
} from '@/core/tamagoshi/Price/Price';
import { PricePropsType } from '@/core/tamagoshi/Price/Price.interfaces';
import { Text } from '@/core/tamagoshi/Text/Text';
import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { type XBorderVat } from '@/domains/productDiscovery/ProductPage/hooks/useXBorderVat';
import type { Price as PriceType } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import { usePromotionLabels } from '@/productDiscovery/ProductPage/common/components/PromoBlock/usePromotionLabels';
import { useDetail } from '@/productDiscovery/ProductPage/hooks/useDetail';
import { useQuantity } from '@/productDiscovery/ProductPage/hooks/useQuantity';
import {
  allTaxExcludedShort,
  allTaxIncludedShort,
} from '@/shoppingCart/CartPage/components/ProductCard/ProductPrices/translations';

import styles from './price.module.scss';

type TaxKey = 'amountWithoutVat' | 'amountWithVat';

export const ProductPrice = ({ vatStatus }: { vatStatus?: XBorderVat }) => {
  const { price, currency } = useDetail();

  const quantity = useQuantity();

  // should we change Detail to use the same type ??
  const tamagoshiCurrency = currency as CurrencyType;

  const { hasPromoLabel } = usePromotionLabels();

  const [displayVatDeductibleInfoEnabled] = useBooleanFlags([
    'displayVatDeductibleInfo',
  ]);

  const mainPriceWithTax = isB2C() || vatStatus === 'nonDeductible';
  const [mainTaxKey, secondaryTaxKey]: TaxKey[] = mainPriceWithTax
    ? ['amountWithVat', 'amountWithoutVat']
    : ['amountWithoutVat', 'amountWithVat'];

  const deductibleVat = vatStatus === 'zero' && displayVatDeductibleInfoEnabled;

  return (
    <div className={styles.wrapper}>
      <div className={styles.priceAndDiscount}>
        <Price
          price={price?.primaryPrice}
          quantity={quantity}
          taxKey={mainTaxKey}
          currency={tamagoshiCurrency}
          format={FormatType.SYMBOL_SUP}
          className={classnames(styles.bigPrice, {
            [styles.promo]: hasPromoLabel,
            [styles.ukSize]: isUkPlatform(),
          })}
        />
        {!!price.discountPercentage && isB2C() && (
          <Text
            type="small"
            className={classnames(styles.discount, {
              [styles.promo]: hasPromoLabel,
            })}
          >
            <span>{`-${price.discountPercentage}%`}</span>
          </Text>
        )}
      </div>

      {!!price.retailPrice && (
        <Price
          taxKey={mainTaxKey}
          quantity={quantity}
          price={price.retailPrice}
          currency={tamagoshiCurrency}
          format={FormatType.DEFAULT}
          className={styles.smallPrice}
          obsolete={true}
        />
      )}

      {isB2B() && (
        <Price
          price={price?.primaryPrice}
          quantity={quantity}
          taxKey={secondaryTaxKey}
          currency={tamagoshiCurrency}
          format={FormatType.SYMBOL}
          className={styles.smallPrice}
          obsolete={deductibleVat}
        />
      )}

      {!!price?.measurementPrice &&
        price?.measurementPrice?.measurementUnit !== 'piece' && (
          <div className={styles.measurementWrapper}>
            <span>
              <Price
                price={price.measurementPrice}
                quantity={1}
                taxKey={mainTaxKey}
                currency={tamagoshiCurrency}
                format={FormatType.SYMBOL}
              />
              {` / ${price.measurementPrice?.measurementUnit}`}
            </span>

            {isB2B() && (
              <span>
                <Price
                  price={price.measurementPrice}
                  quantity={1}
                  taxKey={secondaryTaxKey}
                  currency={tamagoshiCurrency}
                  format={FormatType.SYMBOL}
                />
                {` / ${price.measurementPrice?.measurementUnit}`}
              </span>
            )}
          </div>
        )}
    </div>
  );
};

interface PriceProps extends Omit<PricePropsType, 'value'> {
  quantity: number;
  price: PriceType;
  taxKey: TaxKey;
}

const Price: FC<PriceProps> = ({ price, quantity, taxKey, ...rest }) => {
  const value = (price?.[taxKey] || 0) * quantity;
  const vat = taxKey === 'amountWithVat' ? VatType.INCLUDED : VatType.EXCLUDED;
  const displaysTaxLabels = isB2B();
  const translations = displaysTaxLabels
    ? {
        includedVAT: allTaxIncludedShort,
        excludedVAT: allTaxExcludedShort,
      }
    : {};
  if (value === 0) {
    return null;
  }
  return (
    <TamagoshiPrice
      value={value}
      vat={vat}
      translations={translations}
      {...rest}
    />
  );
};
